import React from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { SIGNING_FLOW_TYPE_MAP } from '../../shared/constants';
import { translate } from '../../utils/translate';
import { GoogleButton } from '../GoogleButton';
import { AppleButton } from '../AppleButton';
import { FacebookButton } from '../FacebookButton';
import { ContinueWithEmailButton } from '../ContinueWithEmailButton';
import { useIdentificationContext } from '../IdentificationContext';
import { MixpanelProviderType } from '../../events/mixpanel';
import { MixpanelSigningFlowType } from '../../events/mixpanel/mixpanel.constants';
import { BigQueryProvider, BigQuerySigningFlowType } from '../../events/bigQuery';
import { globalBigQueryReporter } from '../../events/bigQuery/bigQuery.reporter';
import { globalMixpanelReporter } from '../../events/mixpanel/mixpanel.reporter';
import { globalGrafanaReporter } from '../../events/grafana/grafana.reporter';
import { useSocialSigningHandlers, type SignupHandlers } from './hooks/useSocialSigningHandlers';
import { OrDivider } from './OrDivider';

type OnProviderClick = () => void;

interface SocialSigningFormProps {
    onEmailButtonClick: OnProviderClick;
    onProfileSetup: SignupHandlers['onProfileSetup'];
    onSocialFallback: SignupHandlers['onSocialFallback'];
}

export const SocialSigningForm: React.FC<SocialSigningFormProps> = ({
    onEmailButtonClick,
    onProfileSetup,
    onSocialFallback,
}) => {
    const { onSuccess, onError } = useSocialSigningHandlers({
        onProfileSetup,
        onSocialFallback,
    });
    const { mode } = useIdentificationContext();

    const getMixpanelEnrichment = (
        provider: MixpanelProviderType
    ): { provider: MixpanelProviderType; flow: MixpanelSigningFlowType } => {
        const socialSigningFlow = mode === 'sign_up' ? 'Social Sign Up' : 'Social';
        const standardSigningFlow = mode === 'sign_up' ? 'Sign Up' : 'Sign In';

        return {
            provider,
            flow: provider === 'Standard' ? standardSigningFlow : socialSigningFlow,
        } as const;
    };

    const getBqEnrichment = (
        provider: BigQueryProvider
    ): { provider: BigQueryProvider; flow: BigQuerySigningFlowType } => {
        const socialSigningFlow =
            mode === 'sign_up' ? SIGNING_FLOW_TYPE_MAP.socialSignUp : SIGNING_FLOW_TYPE_MAP.social;
        const standardSigningFlow = mode === 'sign_up' ? SIGNING_FLOW_TYPE_MAP.signUp : SIGNING_FLOW_TYPE_MAP.signIn;

        return {
            provider,
            flow: provider === 'standard' ? standardSigningFlow : socialSigningFlow,
        } as const;
    };

    const handleGoogleClick = () => {
        const mixpanelEnrichment = getMixpanelEnrichment('Google');
        const bqEnrichment = getBqEnrichment('google');

        globalMixpanelReporter.enrichContext(mixpanelEnrichment);
        globalBigQueryReporter.enrichContext(bqEnrichment);
        globalBigQueryReporter.send('google_click', null);
        globalMixpanelReporter.send('Identification - Provider Selected');
        globalGrafanaReporter.count('social_signing.%{platform}.google_click');
    };

    const handleFacebookClick = () => {
        const mixpanelEnrichment = getMixpanelEnrichment('Facebook');
        const bqEnrichment = getBqEnrichment('facebook');

        globalMixpanelReporter.enrichContext(mixpanelEnrichment);
        globalBigQueryReporter.enrichContext(bqEnrichment);
        globalBigQueryReporter.send('facebook_click');
        globalMixpanelReporter.send('Identification - Provider Selected');
        globalGrafanaReporter.count('social_signing.%{platform}.facebook_click');
    };

    const handleAppleClick = () => {
        const mixpanelEnrichment = getMixpanelEnrichment('Apple');
        const bqEnrichment = getBqEnrichment('apple');

        globalMixpanelReporter.enrichContext(mixpanelEnrichment);
        globalBigQueryReporter.enrichContext(bqEnrichment);
        globalBigQueryReporter.send('apple_click', null);
        globalMixpanelReporter.send('Identification - Provider Selected');
        globalGrafanaReporter.count('social_signing.%{platform}.apple_click');
    };

    const handleEmailButtonClick = () => {
        const mixpanelEnrichment = getMixpanelEnrichment('Standard');
        const bqEnrichment = getBqEnrichment('standard');

        globalMixpanelReporter.enrichContext(mixpanelEnrichment);
        globalBigQueryReporter.enrichContext(bqEnrichment);
        globalGrafanaReporter.count('identification.%{platform}.initial.email_click');
        globalMixpanelReporter.send('Identification - Provider Selected');
        globalBigQueryReporter.send('identification.initial.email_click', null);

        onEmailButtonClick();
    };

    return (
        <Stack direction="column" gap="4">
            <GoogleButton onClick={handleGoogleClick} onSuccess={onSuccess} onError={onError} />
            <ContinueWithEmailButton onClick={handleEmailButtonClick} />
            <OrDivider />
            <Stack gap="2">
                <AppleButton onClick={handleAppleClick} onSuccess={onSuccess} onError={onError}>
                    {translate('social.apple')}
                </AppleButton>
                <FacebookButton onClick={handleFacebookClick} onSuccess={onSuccess} onError={onError}>
                    {translate('social.facebook')}
                </FacebookButton>
            </Stack>
        </Stack>
    );
};
